/**
 * docs/ROADMAP.md B2 离线上传队列 — the flush loop.
 *
 * A take only ever enters the queue when the *network* refused it (offline, a
 * dropped connection, a timeout, a 5xx). A 4xx is the server saying the take
 * itself is wrong (TOO_LONG, 授权未签署…), and retrying that would fail forever
 * — those stay on the record page where the student can act on them.
 *
 * Retries are triggered by `online`, by the app coming back to the foreground,
 * by the next app open, by the student's own 「现在重试」, and by a timer for the
 * backoff. Deliberately **not** by Background Sync: iOS Safari does not have it,
 * and a mechanism only half the students get is worse than one they can see.
 *
 * Idempotency: the entry's `id` *is* the `upload_key`, minted once when the take
 * was made. Every attempt sends the same one, so a retry after a response that
 * never arrived resolves to the recording the first attempt already created.
 */
import { ApiError, uploadRecording } from "../api";
import type { ClientMeta } from "../api";
import {
  blobBytes,
  clearFor,
  deleteEntry,
  getEntry,
  indexedDbAvailable,
  isQuotaError,
  listFor,
  putEntry,
  type QueuedUpload,
} from "./db";

/** Backoff between attempts: 5 s, 15 s, 1 min, 5 min, then every 15 min. */
export const BACKOFF_MS = [5_000, 15_000, 60_000, 300_000, 900_000];

export function backoffFor(attempts: number): number {
  return BACKOFF_MS[Math.min(Math.max(attempts, 1), BACKOFF_MS.length) - 1]!;
}

/**
 * Is this failure worth retrying? Offline / dropped connection (status 0),
 * a timeout, a rate limit, anything 5xx. Never a 4xx validation refusal.
 */
export function isRetriableUploadError(err: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (!(err instanceof ApiError)) return true; // an unknown throw is not the take's fault
  const s = err.status;
  if (err.code === "ABORTED") return false;
  return s === 0 || s === 408 || s === 425 || s === 429 || s >= 500;
}

export interface QueueSnapshot {
  /** The logged-in student's entries, oldest first. Never another account's. */
  items: readonly QueuedUpload[];
  /** `true` once the first read of IndexedDB has come back. */
  ready: boolean;
  /** A flush is running right now. */
  flushing: boolean;
  /** The device refused to store the last take (存储空间不足). */
  quotaExceeded: boolean;
}

const EMPTY: QueueSnapshot = { items: [], ready: false, flushing: false, quotaExceeded: false };

let snapshot: QueueSnapshot = EMPTY;
let userId: string | null = null;
let listeners = new Set<() => void>();
let uploadedListeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;
let installed = false;
let flushing = false;
/**
 * Bumped whenever the queue's identity changes (account switch, reset). A flush
 * that was already in flight checks it between entries and stops, so a take can
 * never be uploaded under the account that replaced the one it belongs to.
 */
let epoch = 0;

function emit(): void {
  for (const l of listeners) l();
}

function set(patch: Partial<QueueSnapshot>): void {
  snapshot = { ...snapshot, ...patch };
  emit();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): QueueSnapshot {
  return snapshot;
}

/** Server snapshot for `useSyncExternalStore` (there is no queue during SSR). */
export function getServerSnapshot(): QueueSnapshot {
  return EMPTY;
}

/** Called whenever a queued take reached the server — invalidate the caches. */
export function onUploaded(listener: () => void): () => void {
  uploadedListeners.add(listener);
  return () => uploadedListeners.delete(listener);
}

async function refresh(): Promise<void> {
  if (!userId || !indexedDbAvailable()) {
    set({ items: [], ready: true });
    return;
  }
  try {
    const items = await listFor(userId);
    set({ items, ready: true });
    schedule();
  } catch {
    set({ items: [], ready: true });
  }
}

/** Wake up when the earliest backoff elapses. */
function schedule(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  const due = snapshot.items
    .filter((i) => i.state === "queued" || i.state === "failed")
    .map((i) => i.next_attempt_at);
  if (due.length === 0) return;
  const wait = Math.max(0, Math.min(...due) - Date.now());
  timer = setTimeout(
    () => {
      timer = null;
      void flush();
    },
    Math.min(wait, BACKOFF_MS[BACKOFF_MS.length - 1]!),
  );
}

// --------------------------------------------------------------------------- //
// account scoping
// --------------------------------------------------------------------------- //

/**
 * Point the queue at the logged-in student. Entries belong to a user id, so an
 * account switch on a shared phone hides (and never uploads) the other
 * student's takes — they are still there when that student logs back in.
 */
export function setQueueUser(id: string | null): void {
  if (id === userId) return;
  userId = id;
  epoch += 1;
  set({ items: [], ready: false, quotaExceeded: false });
  void refresh().then(() => {
    if (userId) void flush();
  });
}

export function currentQueueUser(): string | null {
  return userId;
}

// --------------------------------------------------------------------------- //
// enqueue
// --------------------------------------------------------------------------- //

export interface EnqueueInput {
  /** The idempotency key minted for this take. */
  uploadKey: string;
  assignmentId: string;
  sessionId: string;
  title: string;
  task: string;
  durationMs: number;
  sampleRate: number;
  clientMeta: ClientMeta;
  blob: Blob;
  filename: string;
}

export type EnqueueResult =
  | { ok: true }
  /** The device has no room left — the student must stay on the page and retry. */
  | { ok: false; reason: "quota" }
  /** No IndexedDB at all (private mode, ancient browser). */
  | { ok: false; reason: "unavailable" };

/**
 * iOS Safari's IndexedDB can simply never answer (private browsing, a wedged
 * origin). The record screen must not hang on that — a bounded wait turns it
 * into an honest 「存不下」 the student can act on.
 */
const STORE_TIMEOUT_MS = 10_000;

function withTimeout<T>(work: Promise<T>, label: string): Promise<T> {
  return Promise.race([
    work,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), STORE_TIMEOUT_MS),
    ),
  ]);
}

export async function enqueue(input: EnqueueInput): Promise<EnqueueResult> {
  if (!userId || !indexedDbAvailable()) return { ok: false, reason: "unavailable" };
  let audio: ArrayBuffer;
  try {
    audio = await withTimeout(blobBytes(input.blob), "reading the take");
  } catch {
    return { ok: false, reason: "unavailable" };
  }
  const entry: QueuedUpload = {
    id: input.uploadKey,
    user_id: userId,
    assignment_id: input.assignmentId,
    session_id: input.sessionId,
    title: input.title,
    task: input.task,
    duration_ms: input.durationMs,
    sample_rate: input.sampleRate,
    pass: (input.clientMeta.pass as string | undefined) ?? null,
    client_meta: { ...input.clientMeta } as Record<string, unknown>,
    filename: input.filename,
    audio,
    mime: input.blob.type || "audio/wav",
    created_at: Date.now(),
    state: "queued",
    attempts: 0,
    next_attempt_at: Date.now(),
    message: null,
  };
  try {
    await withTimeout(putEntry(entry), "saving the take");
  } catch (err) {
    if (isQuotaError(err)) {
      set({ quotaExceeded: true });
      return { ok: false, reason: "quota" };
    }
    return { ok: false, reason: "unavailable" };
  }
  set({ quotaExceeded: false });
  await refresh();
  void flush();
  return { ok: true };
}

// --------------------------------------------------------------------------- //
// flush
// --------------------------------------------------------------------------- //

async function patch(id: string, changes: Partial<QueuedUpload>): Promise<void> {
  const current = await getEntry(id);
  if (!current) return;
  await putEntry({ ...current, ...changes });
}

/**
 * Walk the queue oldest-first and try each entry whose backoff has elapsed.
 * Runs one at a time: a phone on a weak connection should not fire six
 * multipart uploads at once.
 */
export async function flush(force = false): Promise<void> {
  if (flushing || !userId || !indexedDbAvailable()) return;
  if (!force && typeof navigator !== "undefined" && navigator.onLine === false) return;
  flushing = true;
  set({ flushing: true });
  const startEpoch = epoch;
  try {
    const mine = await listFor(userId);
    for (const entry of mine) {
      if (entry.state === "rejected" || entry.state === "uploading") continue;
      if (!force && entry.next_attempt_at > Date.now()) continue;
      // The account may have changed while the previous upload was in flight.
      if (epoch !== startEpoch || entry.user_id !== userId) break;

      await patch(entry.id, { state: "uploading" });
      await refresh();
      try {
        await uploadRecording({
          assignment_id: entry.assignment_id,
          session_id: entry.session_id,
          elicitation: "natural",
          duration_ms: entry.duration_ms,
          sample_rate: entry.sample_rate,
          blob: new Blob([entry.audio], { type: entry.mime }),
          filename: entry.filename,
          client_meta: entry.client_meta as unknown as ClientMeta,
          upload_key: entry.id,
        });
        await deleteEntry(entry.id);
        for (const l of uploadedListeners) l();
      } catch (err) {
        const attempts = entry.attempts + 1;
        if (isRetriableUploadError(err)) {
          await patch(entry.id, {
            state: "failed",
            attempts,
            next_attempt_at: Date.now() + backoffFor(attempts),
            message: null,
          });
          // Offline again — stop early rather than burning through the queue.
          if (typeof navigator !== "undefined" && navigator.onLine === false) break;
        } else {
          await patch(entry.id, {
            state: "rejected",
            attempts,
            next_attempt_at: Number.MAX_SAFE_INTEGER,
            message:
              err instanceof ApiError
                ? err.message || "这条服务器没有接受，删掉后重录一次。"
                : "这条服务器没有接受，删掉后重录一次。",
          });
        }
      }
      await refresh();
    }
  } finally {
    flushing = false;
    set({ flushing: false });
    await refresh();
  }
}

/** The student's 「现在重试」 — ignores the backoff. */
export async function retryNow(): Promise<void> {
  if (!userId) return;
  const mine = await listFor(userId);
  for (const e of mine) {
    if (e.state === "failed" || e.state === "queued") {
      await patch(e.id, { state: "queued", next_attempt_at: 0 });
    }
  }
  await refresh();
  await flush(true);
}

export async function removeFromQueue(id: string): Promise<void> {
  await deleteEntry(id);
  await refresh();
}

/** 清空待上传 — the caller must have warned the student first. */
export async function clearQueue(): Promise<void> {
  if (!userId) return;
  await clearFor(userId);
  await refresh();
}

// --------------------------------------------------------------------------- //
// triggers
// --------------------------------------------------------------------------- //

/**
 * Install the retry triggers once per page. iOS Safari has no Background Sync,
 * so everything here is a foreground event the page really receives.
 */
export function startQueue(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const wake = () => void flush();
  window.addEventListener("online", wake);
  window.addEventListener("focus", wake);
  window.addEventListener("pageshow", wake);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") wake();
  });
  // …and on this app open.
  wake();
}

/** Test seam — drops listeners, timers and the cached snapshot. */
export function resetQueueForTests(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  installed = false;
  flushing = false;
  epoch += 1;
  userId = null;
  listeners = new Set();
  uploadedListeners = new Set();
  snapshot = EMPTY;
}
